import React, { useMemo } from "react";
import { Alert, Button, Descriptions, Drawer, Empty, Space, Spin, Table, Tabs, Tag, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Link, Paragraph, Text } = Typography;

const taskStatusColorMap = {
    RUNNING: "processing",
    REVIEW_REQUIRED: "gold",
    IMPORTED: "green",
    FAILED: "red",
};

const taskStatusTextMap = {
    RUNNING: "运行中",
    REVIEW_REQUIRED: "需人工处理",
    IMPORTED: "已转记录",
    FAILED: "任务失败",
};

const taskTriggerModeTextMap = {
    MANUAL: "手动触发",
    SCHEDULED: "定时触发",
    RETRY: "失败重试",
};

const taskParseStatusTextMap = {
    PARSED: "图片已解析",
    REVIEW_REQUIRED: "解析需人工修正",
    FAILED: "图片解析失败",
    SKIPPED: "未执行图片解析",
};

const renderMoney = (value) => (value == null ? "—" : Number(value).toFixed(2));
const renderDateTime = (value) => {
    if (!value) {
        return "—";
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss") : value;
};
const renderJsonText = (value) => (value == null ? "—" : JSON.stringify(value, null, 2));
const getTaskChartParse = (task) => task?.raw_payload?.chart_parse || {};
const getTaskChartNormalized = (task) => getTaskChartParse(task)?.normalized || {};
const getTaskSourceWeekMismatch = (task) => getTaskChartParse(task)?.source_week_mismatch || null;
const getTaskDownloadedImages = (task) => (Array.isArray(task?.raw_payload?.downloaded_images) ? task.raw_payload.downloaded_images : []);
const getTaskSnapshotManifest = (task) => task?.raw_payload?.snapshot_manifest || {};
const getImageViewUrl = (image) => image?.url || image?.absolute_src || image?.src || "";
const getImageDisplayName = (image) => {
    if (!image) {
        return "";
    }
    const candidates = [image.filename, image.object_key, image.local_path, image.src, image.absolute_src, image.url].filter(Boolean);
    for (const candidate of candidates) {
        const clean = String(candidate).split("?")[0].split("#")[0];
        const name = clean.split("/").filter(Boolean).pop();
        if (name) {
            return name;
        }
    }
    return "查看原图";
};

const getTaskChartImage = (task) => {
    const chartImage = getTaskSnapshotManifest(task)?.chart_image;
    if (chartImage) {
        return chartImage;
    }
    const downloadedImages = getTaskDownloadedImages(task);
    return (
        downloadedImages.find((image) =>
            String(image?.absolute_src || image?.src || image?.url || "")
                .toLowerCase()
                .includes("fuel_price_analysis")
        ) ||
        downloadedImages[0] ||
        null
    );
};

const buildArtifactRows = (task) => {
    const manifest = getTaskSnapshotManifest(task);
    const rows = [];
    if (task?.screenshot_path) {
        rows.push({ key: "screenshot", type: "页面截图", name: task.screenshot_path, local_path: task.screenshot_path });
    }
    if (task?.page_html_path) {
        rows.push({ key: "html", type: "页面 HTML", name: task.page_html_path, local_path: task.page_html_path });
    }
    const chartImage = getTaskChartImage(task);
    if (chartImage) {
        rows.push({ key: "chart-image", type: "图表图片", ...chartImage, name: getImageDisplayName(chartImage) });
    }
    getTaskDownloadedImages(task).forEach((image, index) => {
        rows.push({ key: `image-${index}`, type: "抓取图片", ...image, name: getImageDisplayName(image) });
    });
    (Array.isArray(manifest.assets) ? manifest.assets : []).forEach((asset, index) => {
        rows.push({ key: `asset-${index}`, type: asset.asset_type || "归档资源", ...asset, name: getImageDisplayName(asset) });
    });
    const seen = new Set();
    return rows.filter((row) => {
        const signature = [row.type, row.name, row.object_key, row.local_path, row.absolute_src, row.url].filter(Boolean).join("|");
        if (seen.has(signature)) {
            return false;
        }
        seen.add(signature);
        return true;
    });
};

const normalizedRows = [
    { key: "week_end_date", label: "源图周结束日", render: (value) => value || "—" },
    { key: "global_price_per_mt", label: "Jet fuel price $/t", render: renderMoney },
    { key: "global_price_per_bbl", label: "Jet fuel price $/bbl", render: renderMoney },
    { key: "north_america_price_per_mt", label: "North America $/t", render: renderMoney },
    { key: "asia_oceania_price_per_mt", label: "Asia & Oceania $/t", render: renderMoney },
    { key: "europe_cis_price_per_mt", label: "Europe & CIS $/t", render: renderMoney },
];

const normalizedColumns = [
    { title: "字段", dataIndex: "label", key: "label", width: 220 },
    { title: "值", dataIndex: "value", key: "value" },
];

const artifactColumns = [
    {
        title: "类型",
        dataIndex: "type",
        key: "type",
        width: 120,
    },
    {
        title: "名称/链接",
        key: "name",
        width: 280,
        render: (_, row) => {
            const url = getImageViewUrl(row);
            const name = row.name || getImageDisplayName(row);
            return url ? (
                <Link href={url} target="_blank" rel="noreferrer" title={url} ellipsis style={{ maxWidth: 260 }}>
                    {name || url}
                </Link>
            ) : (
                <Text ellipsis={{ tooltip: name || "—" }}>{name || "—"}</Text>
            );
        },
    },
    {
        title: "本地/对象路径",
        key: "path",
        width: 360,
        render: (_, row) => (
            <Text type="secondary" ellipsis={{ tooltip: row.object_key || row.local_path || row.src || "—" }}>
                {row.object_key || row.local_path || row.src || "—"}
            </Text>
        ),
    },
];

const TaskRunDetailDrawer = ({ open, taskRun, loading, canCreate, onClose, onParse, onRetry, onCreateDraft }) => {
    const chartParse = getTaskChartParse(taskRun);
    const normalized = getTaskChartNormalized(taskRun);
    const mismatch = getTaskSourceWeekMismatch(taskRun);
    const artifactRows = useMemo(() => buildArtifactRows(taskRun), [taskRun]);
    const canCreateManualDraft =
        canCreate && taskRun?.status === "REVIEW_REQUIRED" && !taskRun?.fuel_price_id && !getTaskSourceWeekMismatch(taskRun);
    const normalizedData = useMemo(
        () =>
            normalizedRows.map((row) => ({
                key: row.key,
                label: row.label,
                value: row.render(normalized[row.key]),
            })),
        [normalized],
    );

    return (
        <Drawer
            title={taskRun ? `抓取任务详情 #${taskRun.id}` : "抓取任务详情"}
            open={open}
            onClose={onClose}
            width={960}
            destroyOnHidden
            extra={
                taskRun && canCreate ? (
                    <Space>
                        {canCreateManualDraft ? (
                            <Button type="primary" size="small" onClick={() => onCreateDraft?.(taskRun.id)}>
                                人工修正入库
                            </Button>
                        ) : null}
                        {taskRun.status !== "IMPORTED" ? (
                            <Button size="small" onClick={() => onParse?.(taskRun.id)} disabled={taskRun.status === "RUNNING"}>
                                重新解析图片
                            </Button>
                        ) : null}
                        <Button size="small" icon={<ReloadOutlined />} onClick={() => onRetry?.(taskRun.id)} disabled={taskRun.status === "RUNNING"}>
                            重试抓取
                        </Button>
                    </Space>
                ) : null
            }
        >
            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", padding: "80px 0" }}>
                    <Spin />
                </div>
            ) : !taskRun ? (
                <Empty description="暂无任务详情" />
            ) : (
                <Tabs
                    items={[
                        {
                            key: "base",
                            label: "基础信息",
                            children: (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    {mismatch ? (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message={`源图周结束日 ${mismatch.source_week_end_date} 与目标周结束日 ${mismatch.target_week_end_date} 不一致`}
                                        />
                                    ) : null}
                                    <Descriptions bordered column={2} size="small">
                                        <Descriptions.Item label="任务编号">{taskRun.id}</Descriptions.Item>
                                        <Descriptions.Item label="任务状态">
                                            <Tag color={taskStatusColorMap[taskRun.status]}>{taskStatusTextMap[taskRun.status] || taskRun.status}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="触发方式">{taskTriggerModeTextMap[taskRun.trigger_mode] || taskRun.trigger_mode}</Descriptions.Item>
                                        <Descriptions.Item label="解析状态">
                                            {taskParseStatusTextMap[chartParse.status] || chartParse.status || "等待图片解析"}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="目标周结束日">{taskRun.candidate_week_end_date || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="发布日期">{taskRun.publish_date || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="源图周结束日">{normalized.week_end_date || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="抓取时间">{renderDateTime(taskRun.source_captured_at)}</Descriptions.Item>
                                        <Descriptions.Item label="任务开始">{renderDateTime(taskRun.capture_started_at)}</Descriptions.Item>
                                        <Descriptions.Item label="任务结束">{renderDateTime(taskRun.capture_finished_at)}</Descriptions.Item>
                                        <Descriptions.Item label="Global $/MT">{renderMoney(taskRun.parsed_global_price_per_mt)}</Descriptions.Item>
                                        <Descriptions.Item label="Global $/bbl">{renderMoney(taskRun.parsed_global_price_per_bbl)}</Descriptions.Item>
                                        <Descriptions.Item label="解析置信度">{taskRun.parse_confidence == null ? "—" : Number(taskRun.parse_confidence).toFixed(2)}</Descriptions.Item>
                                        <Descriptions.Item label="关联记录">
                                            {taskRun.fuel_price_id ? `#${taskRun.fuel_price_id} / ${taskRun.fuel_price_status || "—"}` : "—"}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="目标 URL" span={2}>
                                            <Link href={taskRun.target_url} target="_blank" rel="noreferrer">
                                                {taskRun.target_url}
                                            </Link>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="结果摘要" span={2}>
                                            {taskRun.result_summary || taskRun.error_message || "—"}
                                        </Descriptions.Item>
                                    </Descriptions>
                                    <Table
                                        size="small"
                                        bordered
                                        rowKey="key"
                                        columns={normalizedColumns}
                                        dataSource={normalizedData}
                                        pagination={false}
                                    />
                                </Space>
                            ),
                        },
                        {
                            key: "parse",
                            label: "解析明细",
                            children: (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    {chartParse.status === "REVIEW_REQUIRED" ? (
                                        <Alert type="warning" showIcon message="图表解析不完整，可在人工修正入库时补齐缺失字段后保存为待确认草稿。" />
                                    ) : null}
                                    {chartParse.error ? <Alert type="error" showIcon message={chartParse.error} /> : null}
                                    {Array.isArray(chartParse.warnings) && chartParse.warnings.length ? (
                                        <Alert type="warning" showIcon message={chartParse.warnings.join("；")} />
                                    ) : null}
                                    <Descriptions bordered column={1} size="small">
                                        <Descriptions.Item label="OCR 引擎">{chartParse.engine || "—"}</Descriptions.Item>
                                        <Descriptions.Item label="OCR Markdown">
                                            <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{chartParse.ocr_markdown || "—"}</Paragraph>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="模型输出">
                                            <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{renderJsonText(chartParse.model_output)}</pre>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Space>
                            ),
                        },
                        {
                            key: "artifacts",
                            label: "证据留档",
                            children: (
                                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                                    <Table
                                        size="small"
                                        bordered
                                        rowKey="key"
                                        columns={artifactColumns}
                                        dataSource={artifactRows}
                                        pagination={false}
                                        scroll={{ x: 780 }}
                                        locale={{ emptyText: "暂无留档资源" }}
                                    />
                                    <Descriptions bordered column={1} size="small">
                                        <Descriptions.Item label="页面纯文本">
                                            <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>{taskRun.layout_column_middle_text || "—"}</Paragraph>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Space>
                            ),
                        },
                        {
                            key: "raw",
                            label: "原始 Payload",
                            children: <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{renderJsonText(taskRun.raw_payload)}</pre>,
                        },
                    ]}
                />
            )}
        </Drawer>
    );
};

export default TaskRunDetailDrawer;
